import { useInventoryStore } from '../store/inventorySlice';
import { ITEMS } from '../data/inventory.config';
import { RecoveryItemDelta, RecoveryResult } from '../types/recovery.types';

// Placeholder threshold Phase 9. Calibration Phase 11: design targets 15-25%.
const DEFAULT_THRESHOLD = 0.25;

/**
 * Inventory recovery service on defeat (cf.
 * docs/game-design/death-recovery.md and docs/death-recovery.md).
 *
 * Phase 9 — skip-only: each unit of each stack has an independent chance
 * of being kept. No interactive mini-game (parked post-launch). No
 * algorithmic floor guarantee (parked Phase 11 if the observed standard
 * deviation is deemed too large).
 */
class RecoveryService {
  /**
   * Applies recovery to the current inventory. Mutates the store in place,
   * returns the result for the UI recap.
   */
  applyDefeatRecovery(threshold: number = DEFAULT_THRESHOLD): RecoveryResult {
    const inv = useInventoryStore.getState();
    const slots = inv.slots;

    let totalValueBefore = 0;
    let valueKept = 0;
    const kept: RecoveryItemDelta[] = [];
    const lost: RecoveryItemDelta[] = [];

    for (const slot of slots) {
      if (!slot) {
        continue;
      }
      const def = ITEMS[slot.itemId];
      const unitValue = def?.sellValue ?? 0;
      totalValueBefore += unitValue * slot.quantity;

      let keptQty = 0;
      for (let i = 0; i < slot.quantity; i++) {
        if (Math.random() < threshold) {
          keptQty++;
        }
      }
      const lostQty = slot.quantity - keptQty;

      if (keptQty > 0) {
        kept.push({ itemId: slot.itemId, quantity: keptQty });
        valueKept += keptQty * unitValue;
      }
      if (lostQty > 0) {
        lost.push({ itemId: slot.itemId, quantity: lostQty });
        inv.removeItem(slot.itemId, lostQty);
      }
    }

    // Gold lives in its own counter (not a slot). Apply the same loss rule, but
    // proportionally (gold amounts can be large — a per-unit roll would be a
    // wasteful O(n) loop). Gold's sell value is 1, so it counts toward the recap
    // value totals at face value.
    const goldBefore = inv.gold;
    if (goldBefore > 0) {
      const keptGold = Math.round(goldBefore * threshold);
      const lostGold = goldBefore - keptGold;
      totalValueBefore += goldBefore;
      valueKept += keptGold;
      if (keptGold > 0) {
        kept.push({ itemId: 'gold', quantity: keptGold });
      }
      if (lostGold > 0) {
        lost.push({ itemId: 'gold', quantity: lostGold });
        inv.spendGold(lostGold);
      }
    }

    return {
      kept,
      lost,
      totalValueBefore,
      valueKept,
      threshold,
    };
  }
}

export const recoveryService = new RecoveryService();
